import React, { useState, useEffect } from 'react';
import { predictOccupancyDrop, calculateRenewalChurnProbability, generateDynamicPricingSuggestions, logActivity } from '../../services/supabaseService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import OccupancyPredictionChart from './components/OccupancyPredictionChart';
import ChurnProbabilityList from './components/ChurnProbabilityList';
import DynamicPricingSuggestions from './components/DynamicPricingSuggestions';
import { analyzeOccupancyTrends, calculateChurnRisk, generatePricingRecommendations } from '../../services/aiAnalytics';
import toast from 'react-hot-toast';

const VacancyPredictionAI = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [churnData, setChurnData] = useState([]);
  const [pricingSuggestions, setPricingSuggestions] = useState([]);
  const [activeTab, setActiveTab] = useState('occupancy');
  const [aiEnhancedData, setAiEnhancedData] = useState({
    occupancyAnalysis: null,
    churnAnalysis: null,
    pricingAnalysis: null
  });
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    loadAIData();
  }, []);

  useEffect(() => {
    if (error) {
      toast?.error(error?.message || 'Failed to load data');
    }
  }, [error]);

  const loadAIData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [occupancyPredictions, churnProbability, pricingSugg] = await Promise.all([
        predictOccupancyDrop(),
        calculateRenewalChurnProbability(),
        generateDynamicPricingSuggestions()
      ]);

      setPredictions(occupancyPredictions);
      setChurnData(churnProbability);
      setPricingSuggestions(pricingSugg);

      // Load OpenAI-enhanced analytics
      loadOpenAIAnalytics(occupancyPredictions, churnProbability, pricingSugg);

      await logActivity({
        module: 'dashboard',
        action: 'export',
        entityType: 'ai_predictions',
        entityName: 'Vacancy Prediction AI',
        description: 'Viewed AI predictions dashboard'
      });
    } catch (err) {
      console.error('AI data load error:', err);
      setError(err?.message || 'Failed to load AI predictions');
    } finally {
      setLoading(false);
    }
  };

  const loadOpenAIAnalytics = async (occupancyData, churnDataList, pricingData) => {
    try {
      setAiLoading(true);
      
      const [occupancyAnalysis, churnAnalysis, pricingAnalysis] = await Promise.all([
        analyzeOccupancyTrends({
          occupiedUnits: 85,
          totalUnits: 100,
          historicalData: occupancyData,
          expiringLeases: churnDataList?.length || 0
        })?.catch(err => {
          console.error('Occupancy analysis failed:', err);
          return null;
        }),
        calculateChurnRisk(churnDataList)?.catch(err => {
          console.error('Churn analysis failed:', err);
          return null;
        }),
        generatePricingRecommendations({
          vacantUnits: pricingData?.slice(0, 5) || [],
          marketData: { averageOccupancy: 85, marketTrend: 'stable', demandLevel: 'medium' },
          competitorPricing: { averageRate: 5000, range: { min: 3500, max: 7500 } },
          seasonalTrends: { currentSeason: 'peak', forecast: 'increasing' }
        })?.catch(err => {
          console.error('Pricing analysis failed:', err);
          return null;
        })
      ]);

      setAiEnhancedData({
        occupancyAnalysis,
        churnAnalysis,
        pricingAnalysis
      });
    } catch (err) {
      console.error('OpenAI analytics error:', err);
    } finally {
      setAiLoading(false);
    }
  };

  const tabs = [
    { id: 'occupancy', label: 'Occupancy Prediction', icon: 'TrendingDown' },
    { id: 'churn', label: 'Churn Probability', icon: 'AlertTriangle' },
    { id: 'pricing', label: 'Dynamic Pricing', icon: 'DollarSign' }
  ];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading AI predictions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <p className="text-danger-800">Error: {error}</p>
          <Button onClick={loadAIData} className="mt-4" variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            Vacancy Prediction AI
          </h1>
          <p className="text-muted-foreground">
            AI-powered predictions for occupancy, churn, and pricing optimization
          </p>
        </div>
        <Button onClick={loadAIData} variant="outline" iconName="RefreshCw" iconPosition="left">
          Refresh
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-primary-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Icon name="Sparkles" size={20} className="text-primary-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground mb-1 flex items-center gap-2">
              OpenAI-Powered Analytics
              {aiLoading && <span className="text-xs text-muted-foreground">(Analyzing...)</span>}
            </p>
            <p className="text-sm text-muted-foreground">
              Advanced AI predictions using GPT-5 to analyze occupancy trends, tenant churn risk, and dynamic pricing strategies with real-time market intelligence.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-border overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary-500 text-primary-600 font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span className="whitespace-nowrap">{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'occupancy' && (
          <OccupancyPredictionChart 
            predictions={predictions} 
            aiAnalysis={aiEnhancedData?.occupancyAnalysis}
            loading={aiLoading}
          />
        )}
        {activeTab === 'churn' && (
          <ChurnProbabilityList 
            churnData={churnData} 
            aiAnalysis={aiEnhancedData?.churnAnalysis}
            loading={aiLoading}
          />
        )}
        {activeTab === 'pricing' && (
          <DynamicPricingSuggestions 
            suggestions={pricingSuggestions} 
            aiAnalysis={aiEnhancedData?.pricingAnalysis}
            loading={aiLoading}
          />
        )}
      </div>
    </div>
  );
};

export default VacancyPredictionAI;